import { Component, DestroyRef, inject, Inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { formatAddress, formatDateWithTimezone, getContractPriceAdjustmentIncreaseString, TypedRoute } from '@shared/utils';
import { FormBuilder } from '@angular/forms';
import {
  ContractEditResolve,
  ContractLicensePlate,
  GarageContract,
  GarageContractTerminate,
  GarageContractUpdate,
  LicensePlate,
  ProviderLinkOptions,
} from '@shared/models';
import { ArivoBottomSheetDialogComponent } from '@shared/components/ui-elements';
import { ContractsApiService } from '../contracts-api.service';
import { RouterService, ToastService } from '@shared/services';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { MatCalendar } from '@angular/material/datepicker';
import { AmplitudeEventService, FeatureFlagService, ProviderSettingsService } from '@core/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalizedCurrencyPipe, LocalizedPercentagePipe } from '@core/pipes';
import {
  ContractEditCancelled,
  ContractEditLicensePlateAdded,
  ContractEditLicensePlateRemoved,
  ContractEditSaved,
  ContractEditTerminateCancelled,
  ContractEditTerminateClicked,
  ContractEditTerminateSubmitted,
} from '@integrations/amplitude';

@Component({
  selector: 'app-contract-edit',
  templateUrl: './contract-edit.component.html',
  styleUrl: './contract-edit.component.scss',
})
export class ContractEditComponent {
  @ViewChild('terminateContractDialog') terminateContractDialog!: ArivoBottomSheetDialogComponent;
  @ViewChild('withdrawContractDialog') withdrawContractDialog!: ArivoBottomSheetDialogComponent;
  @ViewChild('endDatepicker') endDatepicker!: MatCalendar<Date>;
  destroyRef = inject(DestroyRef);

  contract!: GarageContract;
  userLicensePlates: ContractLicensePlate[] = [];
  enableContractTermination: boolean = this.featureFlagService.getFeatureFlag('enable_contract_termination');
  showContractReference: boolean = this.featureFlagService.getFeatureFlag('enable_contract_reference');
  earliestTerminationDate: Date = new Date();
  selectedTerminationDate: Date = new Date();
  providerLinks!: ProviderLinkOptions;
  submitted: boolean = false;
  licensePlatesInitialized: boolean = false;

  constructor(
    private router: Router,
    private _datePipe: DatePipe,
    private _formBuilder: FormBuilder,
    private toastService: ToastService,
    private routerService: RouterService,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
    private contractsApiService: ContractsApiService,
    private localizedCurrencyPipe: LocalizedCurrencyPipe,
    private localizedPercentagePipe: LocalizedPercentagePipe,
    private providerSettingsService: ProviderSettingsService,
    private amplitudeEventService: AmplitudeEventService,
    @Inject(ActivatedRoute) private route: TypedRoute<ContractEditResolve>,
  ) {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.contract = data.contractEditApiResponse.garage_contract;
      this.userLicensePlates = data.contractEditApiResponse.license_plates.map((licensePlate: LicensePlate) => {
        if (
          this.contract.license_plates.some(
            (contractLicensePlate) =>
              contractLicensePlate.license_plate === licensePlate.license_plate &&
              contractLicensePlate.license_plate_country === licensePlate.license_plate_country,
          )
        ) {
          return {
            ...licensePlate,
            enabled: true,
          };
        } else {
          return {
            ...licensePlate,
            enabled: false,
          };
        }
      });
      this.providerSettingsService.providerSettings.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.providerLinks = this.providerSettingsService.getLinks();
      });
      if (this.enableContractTermination) {
        this.setTerminationDate();
      }
    });
  }

  contractForm = this._formBuilder.nonNullable.group({
    licensePlates: [<Array<LicensePlate>>[]],
  });

  get enableContractWithdrawal(): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const endDate = this.contract.end ? new Date(this.contract.end) : null;
    if (endDate) {
      endDate.setHours(0, 0, 0, 0);
    }
    const lastDayOfContract = endDate ? endDate.getTime() === today.getTime() : false;
    return this.contract.withdrawable && !lastDayOfContract;
  }

  get isRpcFallbackGarage(): boolean {
    if (!this.contract.garage) {
      return false;
    }
    return (
      !!this.contract.garage.name &&
      !this.contract.garage.city &&
      !this.contract.garage.street &&
      !this.contract.garage.number &&
      !this.contract.garage.zip_code
    );
  }

  updateLicensePlates(licensePlates: LicensePlate[]): void {
    if (!this.licensePlatesInitialized) {
      this.licensePlatesInitialized = true;
    } else {
      this.trackLicensePlateChangesToAmplitude(this.contractForm.controls.licensePlates.value, licensePlates);
    }
    this.contractForm.controls.licensePlates.patchValue(licensePlates);
    this.contractForm.controls.licensePlates.updateValueAndValidity();
  }

  back(): void {
    this.amplitudeEventService.trackEvent(new ContractEditCancelled({ contractType: 'contract' }));
    this.router.navigateByUrl(this.routerService.previousUrl ?? '/dashboard');
  }

  finish(): void {
    this.contractForm.markAllAsTouched();

    if (this.contractForm.valid) {
      this.contract.license_plates = this.contractForm.controls.licensePlates.value;

      let updatePayload: GarageContractUpdate = {
        license_plates: this.contract.license_plates,
      };

      this.contractsApiService
        .updateContract(updatePayload, this.contract.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.amplitudeEventService.trackEvent(new ContractEditSaved({ contractType: 'contract' }));
            this.router.navigateByUrl('/dashboard').finally(() => {
              this.toastService.add({
                message: this.translateService.instant('Contracts.UpdateContractSuccess', {
                  name: this.contract.name,
                }),
                type: 'success',
              });
            });
          },
          error: (error) => {
            if (error.status === 400 && error.error.detail) {
              const detail = error.error.detail;
              for (const key in detail) {
                if (detail.hasOwnProperty(key)) {
                  detail[key].forEach((message: string) => {
                    this.toastService.add({
                      message: message,
                      type: 'error',
                    });
                  });
                }
              }
            } else {
              this.toastService.add({
                message: this.translateService.instant('Contracts.UpdateContractError'),
                type: 'error',
              });
            }
          },
        });
    }
  }

  endDatepickerFilter = (d: Date | null): boolean => {
    const date = d || new Date();

    // Check if the date is the last day of the month
    const isLastDayOfMonth = date.getDate() === new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

    // Filter out dates before the earliest termination date and ensure it's the last day of the month
    return date >= this.earliestTerminationDate && isLastDayOfMonth;
  };

  setTerminationDate(): void {
    if (this.contract.start) {
      let earliestDate = new Date(this.contract.start);
      const currentDate = new Date();

      if (earliestDate < currentDate) {
        earliestDate = currentDate;
      }

      if (this.contract.minimum_duration_months) {
        let minimumContractDurationEndDate = new Date(this.contract.start);
        minimumContractDurationEndDate.setMonth(minimumContractDurationEndDate.getMonth() + this.contract.minimum_duration_months);

        if (earliestDate < minimumContractDurationEndDate) {
          earliestDate = minimumContractDurationEndDate;
        }
      }

      if (this.contract.notice_period_months) {
        let noticePeriodEndDate = new Date();
        //This is so that if it's the 31st, it also allows the 30th of the next month.
        noticePeriodEndDate.setDate(1);
        noticePeriodEndDate.setMonth(noticePeriodEndDate.getMonth() + this.contract.notice_period_months);

        if (earliestDate < noticePeriodEndDate) {
          earliestDate = noticePeriodEndDate;
        }

        // Set to last day of the month
        earliestDate = new Date(earliestDate.getFullYear(), earliestDate.getMonth() + 1, 0);
      }

      this.earliestTerminationDate = earliestDate;
      this.selectedTerminationDate = earliestDate;
    }
  }

  resetTerminationDialog(): void {
    this.submitted = false;
    this.selectedTerminationDate = this.earliestTerminationDate;
    this.endDatepicker.activeDate = this.earliestTerminationDate;
    this.endDatepicker.updateTodaysDate();
  }

  withdrawContract(): void {
    if (this.enableContractWithdrawal) {
      this.contractsApiService
        .withdrawContract(this.contract.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.router.navigateByUrl('/dashboard').finally(() => {
              this.toastService.add({
                message: this.translateService.instant('Contracts.WithdrawContractSuccess', {
                  name: this.contract.name,
                }),
                type: 'success',
              });
            });
          },
          error: (error) => {
            this.toastService.add({
              message: this.translateService.instant('Contracts.WithdrawContractError'),
              type: 'error',
            });
          },
        });
    }
    this.withdrawContractDialog.open = false;
  }

  terminateContract(): void {
    if (!this.enableContractTermination) {
      this.toastService.add({
        message: this.translateService.instant('Contracts.TerminateContractDisabled'),
        type: 'error',
      });
      return;
    }
    if (this.selectedTerminationDate && !this.submitted) {
      let terminatePayload: GarageContractTerminate = {
        end: formatDateWithTimezone(this.selectedTerminationDate),
      };

      const contractDaysLeft = Math.ceil((this.selectedTerminationDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

      this.submitted = true;
      this.contractsApiService
        .terminateContract(terminatePayload, this.contract.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.amplitudeEventService.trackEvent(
              new ContractEditTerminateSubmitted({
                daysLeftInContract: contractDaysLeft,
              }),
            );
            this.router.navigateByUrl('/dashboard').finally(() => {
              this.toastService.add({
                message: this.translateService.instant('Contracts.TerminateContractSuccess', {
                  name: this.contract.name,
                  endDate: this._datePipe.transform(this.selectedTerminationDate),
                }),
                type: 'success',
              });
            });
          },
          error: (error) => {
            if (error.status === 400 && error.error.detail) {
              const detail = error.error.detail;
              for (const key in detail) {
                if (detail.hasOwnProperty(key)) {
                  detail[key].forEach((message: string) => {
                    this.toastService.add({
                      message: message,
                      type: 'error',
                    });
                  });
                }
              }
            } else {
              this.toastService.add({
                message: this.translateService.instant('Contracts.UpdateContractError'),
                type: 'error',
              });
            }
          },
        });

      this.terminateContractDialog.open = false;
    }
  }

  getPriceIncreaseString(garageContract: GarageContract): string | null {
    return getContractPriceAdjustmentIncreaseString(
      garageContract,
      'Contracts.PriceIncreaseInfoEdit',
      this.translateService,
      this.localizedCurrencyPipe,
      this.localizedPercentagePipe,
    );
  }

  get showVpi(): boolean {
    return this.featureFlagService.getFeatureFlag('enable_wipark_vpi');
  }

  openTerminateContractDialog(): void {
    this.amplitudeEventService.trackEvent(new ContractEditTerminateClicked());
    this.terminateContractDialog.open = true;
  }

  openWithdrawContractDialog(): void {
    this.withdrawContractDialog.open = true;
  }

  handleOnClose(): void {
    if (!this.submitted) {
      this.amplitudeEventService.trackEvent(new ContractEditTerminateCancelled());
    }
  }

  trackLicensePlateChangesToAmplitude(previousLicensePlates: LicensePlate[], licensePlates: LicensePlate[]): void {
    let previousEnabledLicensePlates = previousLicensePlates.length;
    let enabledLicensePlates = licensePlates.length;
    let maxEnabledLicensePlates = this.contract.group_limit ?? 0;

    const payload = this.amplitudeEventService.buildContractParkingSpacesPayload(maxEnabledLicensePlates, enabledLicensePlates);

    if (previousEnabledLicensePlates < enabledLicensePlates) {
      this.amplitudeEventService.trackEvent(new ContractEditLicensePlateAdded(payload));
    } else if (previousEnabledLicensePlates > enabledLicensePlates) {
      this.amplitudeEventService.trackEvent(new ContractEditLicensePlateRemoved(payload));
    }
  }

  protected readonly formatAddress = formatAddress;
}
